<template>
  <CustomContainer :showAddButton="false">
    <div class="flex flex-col gap-4 p-2">
      <div class="flex items-center justify-between border_bottom pb-4">
        <div class="flex items-center gap-2">
          <ThemeImage
            :icon="config.icon"
            :alt="`${config.type}_icon`"
            class="size-5"
          />
          <h3 class="text-md font-semibold">
            {{ isEdit ? "Edit" : "" }} {{ config.label }}
          </h3>
        </div>
        <ThemeImage
          v-if="isEdit"
          :icon="imageNames.DELETE_GREY"
          alt="delete_icon"
          class="size-4 cursor-pointer"
          @click="handleDelete"
        />
      </div>

      <div class="form_col gap-2">
        <label for="action_name" class="secondary_color text-[13px]">Name *</label>
        <a-input
          id="action_name"
          v-model:value="formData.action_name"
          placeholder="Enter name"
          class="input_medium"
        />
      </div>

      <div v-if="config.type === voiceAgentActionTypes.SCHEDULE_MEETING" class="form_col gap-2">
        <label for="trigger_prompt" class="secondary_color text-[13px]"
          >When should this action be triggered *</label
        >
        <a-textarea
          id="trigger_prompt"
          v-model:value="formData.trigger_prompt"
          :rows="4"
          placeholder="Describe the caller request that should trigger this action (e.g., I'd like to book a demo with the Sales team)"
          class="textarea_large"
        />
      </div>

      <div v-if="config.type === voiceAgentActionTypes.SCHEDULE_MEETING" class="form_col gap-2">
        <label for="meeting_agent" class="secondary_color text-[13px]">Meeting Agent *</label>
        <a-select
          ref="meetingAgentSelectRef"
          id="meeting_agent"
          v-model:value="formData.meeting_agent"
          :options="meetingAgentOptions"
          :loading="meetingAgentsLoading"
          class="w-full"
          placeholder="Select meeting agent"
          show-search
          :filter-option="false"
          @search="handleMeetingAgentSearch"
          @change="handleMeetingAgentChange"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_arrow"
              class="size-4"
            />
          </template>
          <template #dropdownRender="{ menuNode: menu }">
            <v-nodes :vnodes="menu" />
            <div class="select_vnode" @click="handleAddMeetingAgent">
              <ThemeImage
                class="size-[14px]"
                :icon="imageNames.ADD_GREY"
                alt="add_icon"
              />
              <span class="text-sm">Add Meeting Agents</span>
            </div>
          </template>
        </a-select>
      </div>

      <div v-if="config.type === voiceAgentActionTypes.SCHEDULE_MEETING" class="form_col gap-2">
        <label for="action_timezone" class="secondary_color text-[13px]">Timezone Handling</label>
        <a-select
          id="action_timezone"
          v-model:value="formData.action_timezone"
          :options="timezoneOptions"
          class="w-full"
          placeholder="Select timezone handling"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_arrow"
              class="size-4"
            />
          </template>
        </a-select>
        <span class="text-xs secondary_color">
          Choose how you want to handle timezone selection for scheduling
          meetings.
        </span>
      </div>

      <div v-if="config.type === voiceAgentActionTypes.QUALIFICATION_AGENT" class="form_col gap-2">
        <label for="qualification_criteria" class="secondary_color text-[13px]"
          >Define qualification criteria *</label
        >
        <a-textarea
          id="qualification_criteria"
          v-model:value="formData.qualification_criteria"
          :rows="4"
          placeholder="Some criteria to be entered"
          class="textarea_large"
        />
      </div>

      <div class="flex justify-start">
        <a-button
          class="primary_medium"
          @click="handleSave"
          :disabled="!isFormValid"
          :loading="isSaving"
        >
          Save
        </a-button>
      </div>
    </div>

    <AgentTypeSelector
      :visible="showAgentTypeSelector"
      @update:visible="handleAgentTypeSelectorVisibility"
      @selected="handleAgentTypeSelected"
    />

    <CreateAgentDrawer
      :drawerVisible="showCreateAgentDrawer"
      :agent-type="selectedAgentType"
      @update:modelValue="showCreateAgentDrawer = false"
      @agent-created="handleAgentCreated"
    />
  </CustomContainer>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineComponent} from "vue";
import { message } from "ant-design-vue";
import { debounce } from "lodash";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomContainer from "@/src/components/CustomContainer.vue";
import AgentTypeSelector from "@/src/views/meetings/meeting_agent/AgentTypeSelector.vue";
import CreateAgentDrawer from "@/src/views/meetings/meeting_agent/CreateAgentDrawer.vue";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";
import DashboardService from "@/src/api/DashboardService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  voiceAgentActionTypes,
  timezoneHandlingOptions,
  timezoneHandlingTypes,
} from "@/src/constants/voiceAgentConstants.js";
import { sanitizeActionName } from "@/src/utils/utils.js";

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },
  actionData: {
    type: Object,
    default: null,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  agentId: {
    type: Number,
    default: null,
  },
});

const VNodes = defineComponent({
  props: {
    vnodes: {
      type: Object,
      required: true,
    },
  },

  render() {
    return this.vnodes;
  },
});

const emit = defineEmits(["save", "delete"]);

const isSaving = ref(false);
const timezoneOptions = ref(timezoneHandlingOptions);
const meetingAgents = ref([]);
const meetingAgentsLoading = ref(false);
const selectedMeetingAgent = ref(null);
const defaultTimezone = timezoneHandlingTypes.ASK_USER;
const showAgentTypeSelector = ref(false);
const showCreateAgentDrawer = ref(false);
const selectedAgentType = ref(null);
const meetingAgentSelectRef = ref(null);

const formData = ref({
  action_name: "",
  trigger_prompt: "",
  meeting_agent: "",
  action_timezone: defaultTimezone,
  qualification_criteria: "",
  type: props.config.type,
});

const meetingAgentOptions = computed(() => {
  const options = meetingAgents.value.map(agent => ({
    value: agent.public_link,
    label: agent.name,
    timezone: agent.owner_timezone,
    agentData: agent,
  }));

  if (props.actionData?.meeting_agent && props.actionData?.meeting_agent_name) {
    const exists = options.find(opt => opt.value === props.actionData.meeting_agent);
    if (!exists) {
      options.unshift({
        value: props.actionData.meeting_agent,
        label: props.actionData.meeting_agent_name,
        timezone: props.actionData.owner_timezone || props.actionData.action_timezone,
        agentData: {
          public_link: props.actionData.meeting_agent,
          name: props.actionData.meeting_agent_name,
          owner_timezone: props.actionData.owner_timezone || props.actionData.action_timezone,
        },
      });
    }
  }

  return options;
});

const isFormValid = computed(() => {
  if (!formData.value.action_name || !formData.value.action_name.trim()) {
    return false;
  }

  if (props.config.type === voiceAgentActionTypes.SCHEDULE_MEETING) {
    return (
      formData.value.trigger_prompt &&
      formData.value.trigger_prompt.trim() !== "" &&
      formData.value.meeting_agent &&
      formData.value.meeting_agent.trim() !== ""
    );
  }

  if (props.config.type === voiceAgentActionTypes.QUALIFICATION_AGENT) {
    return (
      formData.value.qualification_criteria &&
      formData.value.qualification_criteria.trim() !== ""
    );
  }

  return false;
});

const fetchMeetingAgents = async (searchTerm = "") => {
  try {
    meetingAgentsLoading.value = true;
    const params = {
      page: 1,
      page_size: 20,
    };
    if (searchTerm) {
      params.search_query = searchTerm;
    }
    const response = await DashboardService.fetchMeetingAgents(params);
    if (response?.success && response?.agents) {
      meetingAgents.value = response.agents;
    }
  } catch (error) {
    console.error("Error fetching meeting agents:", error);
  } finally {
    meetingAgentsLoading.value = false;
  }
};

const handleMeetingAgentSearch = debounce((searchTerm) => {
  fetchMeetingAgents(searchTerm);
}, 300);

const handleMeetingAgentChange = (value) => {
  try {
    const agent = meetingAgentOptions.value.find(opt => opt.value === value);
    if (agent) {
      selectedMeetingAgent.value = agent.agentData;
    }
  } catch (error) {
    console.error("Error handling meeting agent change:", error);
  }
};

const handleSave = async () => {
  try {
    if (!isFormValid.value) {
      return;
    }

    if (!props.agentId) {
      message.error("Agent ID is missing");
      return;
    }

    isSaving.value = true;

    const sanitizedActionName = sanitizeActionName(formData.value.action_name);
    
    if (!sanitizedActionName) {
      message.error("Invalid action name. Use only letters, numbers, underscores, and dashes.");
      isSaving.value = false;
      return;
    }

    let payload = {
      voice_agent_id: props.agentId,
      action_type: formData.value.type,
      action_name: sanitizedActionName,
      action_details: {},
    };

    if (formData.value.type === voiceAgentActionTypes.SCHEDULE_MEETING) {
      payload.action_details = {
        meeting_agent: formData.value.meeting_agent.trim(),
        trigger_prompt: formData.value.trigger_prompt.trim(),
        action_timezone: formData.value.action_timezone,
      };
      
      if (formData.value.action_timezone === timezoneHandlingTypes.FOLLOW_MEETING_AGENT && selectedMeetingAgent.value?.owner_timezone) {
        payload.action_details.owner_timezone = selectedMeetingAgent.value.owner_timezone;
      }
    } else if (formData.value.type === voiceAgentActionTypes.QUALIFICATION_AGENT) {
      payload.action_details = {
        qualification_criteria: formData.value.qualification_criteria.trim(),
        type: "string",
      };
    }

    let response;
    if (props.isEdit && formData.value.id) {
      response = await VoiceAgentService.updateVoiceAction(
        formData.value.id,
        payload
      );
    } else {
      response = await VoiceAgentService.createVoiceAction(payload);
    }

    if (response?.success) {
      const savedData = {
        id: response.id || formData.value.id,
        name: sanitizedActionName,
        type: formData.value.type,
        created_at:
          response.created_at ||
          formData.value.created_at ||
          new Date().toISOString(),
        updated_at: response.updated_at || new Date().toISOString(),
      };

      if (formData.value.type === voiceAgentActionTypes.SCHEDULE_MEETING) {
        savedData.trigger_prompt = formData.value.trigger_prompt;
        savedData.meeting_agent = formData.value.meeting_agent;
        savedData.meeting_agent_name = selectedMeetingAgent.value?.name || "";
        savedData.action_timezone = formData.value.action_timezone;
      } else if (formData.value.type === voiceAgentActionTypes.QUALIFICATION_AGENT) {
        savedData.qualification_criteria =
          formData.value.qualification_criteria;
      }

      emit("save", savedData);
    } else {
      message.error(
        `Failed to ${props.isEdit ? "update" : "create"} action`
      );
    }
  } catch (error) {
    console.error(
      `Error ${props.isEdit ? "updating" : "creating"} action:`,
      error
    );
    message.error(`Failed to ${props.isEdit ? "update" : "create"} action`);
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async () => {
  try {
    if (!formData.value.id) {
      emit("delete");
      return;
    }
    const response = await VoiceAgentService.deleteVoiceAction(
      formData.value.id
    );
    if (response?.success) {
      emit("delete");
    }
  } catch (error) {
    console.error("Error deleting action:", error);
    message.error("Failed to delete action");
  }
};

const handleAddMeetingAgent = () => {
  try {
    if (meetingAgentSelectRef.value) {
      meetingAgentSelectRef.value.blur();
    }
    showAgentTypeSelector.value = true;
  } catch (error) {
    console.error("Error opening agent type selector:", error);
  }
};

const handleAgentTypeSelectorVisibility = (value) => {
  try {
    showAgentTypeSelector.value = value;
  } catch (error) {
    console.error("Error handling agent type selector visibility:", error);
  }
};

const handleAgentTypeSelected = (type) => {
  try {
    if (!type) {
      return;
    }
    selectedAgentType.value = type;
    showAgentTypeSelector.value = false;
    showCreateAgentDrawer.value = true;
  } catch (error) {
    console.error("Error handling agent type selection:", error);
  }
};

const handleAgentCreated = async (newAgent) => {
  try {
    await fetchMeetingAgents();
  } catch (error) {
    console.error("Error handling agent creation:", error);
    message.error("Failed to update meeting agent list");
  }
};

const populateFormData = (newData) => {
  try {
    if (newData) {
      formData.value = {
        id: newData.id,
        action_name: newData.name || newData.action_name || "",
        type: newData.type || props.config.type,
        trigger_prompt: newData.trigger_prompt || "",
        meeting_agent: newData.meeting_agent || "",
        action_timezone: newData.action_timezone || defaultTimezone,
        qualification_criteria: newData.qualification_criteria || "",
        created_at: newData.created_at,
        updated_at: newData.updated_at,
      };
      
      if (newData.meeting_agent && newData.meeting_agent_name) {
        selectedMeetingAgent.value = {
          public_link: newData.meeting_agent,
          name: newData.meeting_agent_name,
          owner_timezone: newData.owner_timezone || newData.action_timezone,
        };
      }
    } else {
      formData.value = {
        action_name: "",
        trigger_prompt: "",
        meeting_agent: "",
        action_timezone: defaultTimezone,
        qualification_criteria: "",
        type: props.config.type,
      };
    }
  } catch (error) {
    console.error("Error populating form data:", error);
  }
};
defineExpose({
  formData,
});

watch(() => props.actionData, populateFormData, { immediate: true, deep: true });

onMounted(() => {
  fetchMeetingAgents();
});
</script>

